import moment from "moment-timezone";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { getBookingPrice } from "./api";
import { UserContext } from "../../App";
import { db } from "../../index";
import { concatenateProperties } from "../search";

export const _defaultBooking = {
  id: "",
  name: "",
  created_at: moment().utc().unix(),
  dontSendUpdateMailToCustomer: false,
  dontSendWhatsappToCustomer: false,

  // Customer
  provider: {
    id: "",
    name: "",
    email: "",
    tel: "",
  },

  customer_data: {
    id: "",
    firstname: "",
    lastname: "",
    email: "",
    phone: ""
  },

  firstname: "",
  lastname: "",
  email: "",
  phone: "",

  contact_email: "",
  contact_phone: "",

  hide_phone: false,

  ref_code: "",
  flight_number: "",
  passengers: "",
  bags: "",

  // Pickup/Dropoff
  pickup: {
    address: "",
    full_address: "",
  },
  step_address: "",
  date: moment().utc().unix(),
  time: "",

  dropoff: {
    address: "",
    full_address: "",
  },

  distance: "",
  duration: "",

  // Driver
  driver: {
    id: "",
    name: "",
    phone: "",
    macaron_number: "",
    company: {
      name: "",
      id: "",
    },
  },

  // Details
  vehicle: "berline",
  type: "one_way",
  num_hours: "",
  tarif: "",
  payment_method: "transfer",
  status: "Unpaid",
  amount: 0,
  driver_price: 0,
  additional_info: "",
  driverJwt: "",
  clientJwt: "",
  event: "",
  folder_id: "",

  // Track

  clientOnBoardInfos: null,
  noShow: null,
  onPlaceInfos: null,
  onTheWayInfos: null,
  stopList: null,
  tripEndInfos: null,
  sharedBy: null,
};
export const defaultBooking = () => {
  return {
    ..._defaultBooking,
    provider: { ..._defaultBooking.provider },
    customer_data: { ..._defaultBooking.customer_data },
    pickup: { ..._defaultBooking.pickup },
    dropoff: { ..._defaultBooking.dropoff },
    driver: { ..._defaultBooking.driver },
  };
};

export function getPriceErrorMessage(
  pickupAddress,
  dropoffAddress,
  bookingType,
  numHours,
  vehicle,
  tarif,
  requireTarif = true
) {
  if (!pickupAddress) return "Adresse de départ manquante";
  if (!dropoffAddress) return "Adresse d'arrivée manquante";
  if (!vehicle) return "Véhicule manquant";
  if (!bookingType) return "Type de course manquant";
  if (requireTarif && !tarif) return "Tarif manquant";
}

export function useBookingPrice(
  jwt,
  pickupAddress,
  dropoffAddress,
  bookingType,
  numHours,
  vehicle,
  tarifId,
  productId = null,
  requireTarif = true,
  pickupPlaceId = null,
  dropoffPlaceId = null,
  pickupLat = null,
  pickupLon = null,
  dropoffLat = null,
  dropoffLon = null
  ) {
  const [loading, setLoading] = useState(0);
  const [prices, setPrices] = useState({
    partner: 0,
    client: 0,
    distance: 0,
    duration: 0,
  });

  const errorMessage = useMemo(() => {
    return getPriceErrorMessage(
      pickupAddress,
      dropoffAddress,
      bookingType,
      numHours,
      vehicle,
      tarifId,
      requireTarif
    );
  }, [
    bookingType,
    dropoffAddress,
    numHours,
    pickupAddress,
    tarifId,
    vehicle,
    requireTarif,
  ]);

  useEffect(() => {
    if (errorMessage) return;

    setLoading((old) => old + 1);
    getBookingPrice(
      jwt,
      pickupAddress,
      dropoffAddress,
      bookingType,
      numHours,
      vehicle,
      tarifId,
      productId,
      pickupPlaceId,
      dropoffPlaceId,
      // Build coords objects only when calling the API
      pickupLat && pickupLon ? { lat: pickupLat, lon: pickupLon } : null,
      dropoffLat && dropoffLon ? { lat: dropoffLat, lon: dropoffLon } : null
    ).then((price) => {
      setPrices(price);
      setLoading((old) => old - 1);
    });
  }, [
    bookingType,
    dropoffAddress,
    errorMessage,
    jwt,
    numHours,
    pickupAddress,
    tarifId,
    productId,
    vehicle,
    pickupPlaceId,
    dropoffPlaceId,
    pickupLat,
    pickupLon,
    dropoffLat,
    dropoffLon,
  ]);

  return { loading, prices, error: errorMessage };
}

function transformBookingForTable(booking) {
  let now = moment().tz("Europe/Paris").unix();
  let sortDate =
    moment(
      moment(booking.date * 1000).format("YYYY-MM-DD") +
        "T" +
        booking.time +
        ":00"
    ).unix() - moment().tz("Europe/Paris").startOf("day").unix();

  // Past dates (before start of day) appear last (-10 * -10^6 ~= very large number)
  if (sortDate < 0) {
    sortDate *= -1000000;
  }

  // Invalid dates appear first
  if (isNaN(sortDate)) sortDate = -1;

  let departure = moment(
    moment(booking.date * 1000).format("YYYY-MM-DD") +
      "T" +
      booking.time +
      ":00"
  ).unix();

  if (departure <= now + 14400 && !booking?.driver?.name) {
    booking.urgent = true;
    sortDate = -2;
  }

  if (
    departure <= now + 2400 &&
    (!booking.progress_status || booking.progress_status < 1)
  ) {
    booking.driverUrgent = true;
  }

  return {
    ...booking,
    name: booking.firstname + " " + booking.lastname,
    published_date: booking.published
      ? moment(booking.published).format("DD MMM YYYY HH:mm")
      : "",
    pickup_date: {
      moment: moment(
        moment(booking.date * 1000)
          .tz("Europe/Paris")
          .format("YYYY-MM-DD") +
          " " +
          booking.time
      ),
      date: moment(
        moment(booking.date * 1000)
          .tz("Europe/Paris")
          .format("YYYY-MM-DD") +
          " " +
          booking.time
      ).format("DD MMM YYYY HH:mm"),
      sortDate: sortDate,
    },
    created_at: moment(booking.created_at * 1000)
      .tz("Europe/Paris")
      .format("YYYY-MM-DD HH:mm"),
    driver_name: booking.driver ? booking.driver.name : "",
    driver_phone: booking.driver ? booking.driver.phone : "",
    macaron_number: booking.driver ? booking.driver.macaron_number : "",
    id: booking.id ? booking.id : booking.key,
    pickup_address: booking.pickup.address.replace(/\\/g, ""),
    dropoff_address: booking.dropoff.address.replace(/\\/g, ""),
    status: (
      <span className="kt-badge kt-badge--inline kt-badge--warning">
        {booking.status}
      </span>
    ),
  };
}

export function useBookings(
  startDate = null,
  endDate = null,
  tableDisplay = false,
  folderId = null
) {
  const { userInfos } = useContext(UserContext);
  const [bookings, setBookings] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let bookingsRef = db
      .ref("users/" + userInfos.uid + "/booking")
      .orderByChild("date");
    if (startDate) {
      bookingsRef = bookingsRef.startAt(moment(startDate).unix());
    }
    if (endDate) {
      bookingsRef = bookingsRef.endAt(moment(endDate).unix());
    }

    if (folderId) {
      bookingsRef = db
        .ref("users/" + userInfos.uid + "/booking")
        .orderByChild("folder_id")
        .equalTo(folderId);
    }

    bookingsRef.once("value").then((snapshot) => {
      let res = [];
      snapshot.forEach(function (childSnapshot) {
        let booking = {
          id: childSnapshot.key,
          ...childSnapshot.val(),
        };

        if (userInfos?.child?.rights?.providersFilter?.length) {
          if (
            !booking?.provider?.id ||
            !userInfos.child.rights.providersFilter.includes(
              booking.provider.id
            )
          ) {
            return;
          }
        }

        // Partner access: only show bookings assigned to the allowed companies
        if (userInfos?.child?.rights?.companyFilter?.length) {
          if (
            !booking?.driver?.company?.id ||
            !userInfos.child.rights.companyFilter.includes(
              booking.driver.company.id
            )
          ) {
            return;
          }
        }

        if (tableDisplay) {
          booking = transformBookingForTable(booking);
        }

        res.push(booking);
      });

      setBookings(res);
      setLoaded(true);
    });
  }, [startDate, endDate, tableDisplay, folderId, userInfos]);

  return { bookings, loaded };
}

function transformBookingToSearchObj(booking) {
  const tableBooking = transformBookingForTable(booking);
  const { fields, values } = concatenateProperties(tableBooking);

  return {
    id: booking.id,
    title: booking.name,
    description: values,
    fields: fields,
    dbField: "booking",
    objType: "Réservation",
    url: `/booking/${booking.id}`,
  };
}

export async function getBookingsSearchList(userInfos) {
  let res = [];

  let startDate = moment().subtract(1, "month").unix();

  let bookingsRef = db
    .ref("users/" + userInfos.uid + "/booking")
    .orderByChild("date")
    .startAt(startDate);
  let snapshot = await bookingsRef.once("value");
  snapshot.forEach(function (childSnapshot) {
    res.push(transformBookingToSearchObj(childSnapshot.val()));
  });

  return res;
}
